import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error occurred';
  const { status, data } = error.response;
  if (data?.detail) {
    if (Array.isArray(data.detail)) {
      const first = data.detail[0];
      if (typeof first === 'object') return `${first.loc?.[first.loc.length - 1] || 'Field'}: ${first.msg || 'Invalid value'}`;
      return String(first);
    }
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  return `Request failed with status ${status}`;
};

const getDateString = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const ChallanForm = ({ onClose, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const today = new Date();

  const [formData, setFormData] = useState({
    customer_id: '',
    issue_date: getDateString(today),
    expected_delivery_date: getDateString(today),
    vehicle_no: '',
    driver_name: '',
    notes: '',
  });
  const [items, setItems] = useState([{ product_id: '', unit_id: '', quantity: '1.00', description: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          api.get('/customers?is_active=true'),
          api.get('/products?limit=1000'),
        ]);
        setCustomers(cRes.data.items ?? cRes.data);
        setProducts((pRes.data.items ?? pRes.data.products ?? []).filter((p) => p.is_active));
      } catch (err) { console.error('Failed to load data', err); }
    })();
  }, []);

  const handleProductChange = (index, productId) => {
    const newItems = [...items];
    newItems[index].product_id = productId;
    newItems[index].unit_id = '';
    const product = products.find((p) => p.id === productId);
    if (product) {
      const defaultUnit = (product.units || []).find((u) => u.is_default);
      if (defaultUnit) newItems[index].unit_id = defaultUnit.id;
    }
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { product_id: '', unit_id: '', quantity: '1.00', description: '' }]);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.customer_id) { setError('Please select a customer'); return; }
    if (items.length === 0 || !items[0].product_id) { setError('Please add at least one item'); return; }
    for (let i = 0; i < items.length; i++) {
      if (!items[i].product_id) { setError(`Item ${i + 1}: Please select a product`); return; }
      if (String(items[i].quantity).trim() === '') { setError(`Item ${i + 1}: Quantity is required`); return; }
    }
    setLoading(true);
    try {
      await api.post('/delivery-challans', {
        customer_id: formData.customer_id,
        issue_date: formData.issue_date || getDateString(new Date()),
        expected_delivery_date: formData.expected_delivery_date || null,
        vehicle_no: formData.vehicle_no || null,
        driver_name: formData.driver_name || null,
        notes: formData.notes || null,
        items: items.map((it) => ({
          product_id: it.product_id,
          unit_id: it.unit_id || null,
          quantity: String(it.quantity).trim(),
          description: it.description || null,
        })),
      });
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
      console.error('Challan create error:', err);
    } finally {
      setLoading(false);
    }
  };

  const itemInputClass = 'w-full bg-white border border-[#d1d5db] rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none';

  return (
    <ModalShell
      title="Create Delivery Challan"
      onClose={onClose}
      maxWidth="max-w-4xl"
      dataTestId="challan-form-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors" data-testid="cancel-button">Cancel</button>
          <button type="submit" form="challan-form" disabled={loading} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50" data-testid="save-challan-button">{loading ? 'Creating...' : 'Create Challan'}</button>
        </div>
      }
    >
      <form id="challan-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="form-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Customer {requiredMark}</label>
            <select className={selectClass} value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} required data-testid="customer-select">
              <option value="">Select Customer</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Issue Date</label>
            <input type="date" className={inputClass} value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} data-testid="issue-date-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelClass}>Expected Delivery</label>
            <input type="date" className={inputClass} value={formData.expected_delivery_date} onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })} data-testid="expected-delivery-input" />
          </div>
          <div>
            <label className={labelClass}>Vehicle No.</label>
            <input type="text" className={inputClass} value={formData.vehicle_no} onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value })} placeholder="LHR-1234" data-testid="vehicle-input" />
          </div>
          <div>
            <label className={labelClass}>Driver Name</label>
            <input type="text" className={inputClass} value={formData.driver_name} onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })} data-testid="driver-input" />
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-semibold text-[#1a1a2e]">Items</h3>
            <button type="button" onClick={addItem} className="text-[#4A7AE0] hover:underline text-sm font-medium flex items-center gap-1" data-testid="add-item-button">
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          <div className="space-y-2">
            {items.map((item, index) => {
              const selectedProduct = products.find((p) => p.id === item.product_id);
              const productUnits = selectedProduct?.units || [];
              const baseUnitLabel = selectedProduct?.base_unit || selectedProduct?.unit || '';
              return (
                <div key={index} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <select className={itemInputClass} value={item.product_id} onChange={(e) => handleProductChange(index, e.target.value)} required data-testid={`product-select-${index}`}>
                      <option value="">Select Product</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name} (Stock: {p.current_stock} {p.base_unit || p.unit})</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <select className={`${itemInputClass} disabled:bg-[#f3f4f6]`} value={item.unit_id} onChange={(e) => handleItemChange(index, 'unit_id', e.target.value)} disabled={!item.product_id} data-testid={`unit-select-${index}`}>
                      <option value="">{baseUnitLabel || 'Base unit'}</option>
                      {productUnits.map((u) => <option key={u.id} value={u.id}>{u.unit_name} ({u.conversion_factor} {baseUnitLabel})</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <input type="number" step="0.01" min="0.01" placeholder="Qty" className={itemInputClass} value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} required data-testid={`quantity-input-${index}`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)} className="text-[#EF4444] hover:bg-[#FEE2E2] p-1.5 rounded-lg" data-testid={`remove-item-${index}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <label className={labelClass}>Notes</label>
          <textarea className={textareaClass} rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} data-testid="notes-input" placeholder="Handling instructions, delivery notes..." />
        </div>
      </form>
    </ModalShell>
  );
};

export default ChallanForm;
